import { Formik } from "formik";
import * as Yup from "yup";
import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { AuthContext } from "../../Providers/UserProvider";
import { hasShopUserClaims } from "../../Providers/shopAuth";
import { fetchShopJobsWithTechs } from "./fetchShopJobs";
import CreateSotSwitcher from "./CreateSotSwitcher";
import JobsSotSwitcher from "./JobsSotSwitcher";
import TrueEmptySwitcher from "./TrueEmptySwitcher";
import DemoWalk from "./DemoWalk";
import {
  shopDemoWalkOpenTab,
  shouldOpenShopActiveTab,
  shouldOpenShopCompleteTab,
  shouldResetShopWalkResidue,
} from "./demoWalk";
import { format } from "date-fns";
import JobsTableComponent, { shopJobVehicleLabel } from "./JobsTable";
import CancelledJobsComponent from "./CancelledTable";
import Loader from "../../Shared/Loader";
import Calendar from "../../Shared/Calendar";
import { showError } from "../../utils/ui";
import { useHistory, useLocation } from "react-router-dom";
import StatusEmptyCard from "./StatusEmptyCard";
import CreateJobModal from "./CreateJobModal";
import {
  shopCreateFixtureKind,
  shopCreateFixtureNotice,
  shopCreatePreviewSurface,
  shopCreateSotActive,
  shopTrueEmptyFixtureNotice,
  shopTrueEmptyPreviewSurface,
  shopTrueEmptySotActive,
  shopTrueEmptyWalkKind,
} from "./createShopJob";
import {
  JOBS_TAB_EMPTY,
  SHOP_JOBS_PREVIEW_ERROR,
  SHOP_JOBS_PREVIEW_NOTICE,
  SHOP_JOBS_SOT_ERROR,
  SHOP_JOBS_SOT_NOTICE,
  jobsEmptyCopyKind,
  jobsHeaderDetail,
  jobsListCopy,
  isShopWalkSot,
  shopJobsFixtureNotice,
  shopJobsFixtureSurface,
  shopJobsPreviewJobs,
  shopJobsSotActive,
  shopDisplayRegionId,
  shopLiveWriteRegionError,
  shopRegionLabel,
  shopSearchHasStatus,
  shopSearchWithStatus,
  shopWalkStatusFromSot,
  shopWalkStatusHref,
  statusSurfaceState,
} from "./jobsSurface";
import RegionWalkSwitcher from "./RegionWalkSwitcher";

function shopEditDateInputValue(stored) {
  if (typeof stored === "string") {
    const legacy = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/.exec(stored);
    if (legacy) {
      const [year, month, day] = legacy[1].split("-").map(Number);
      const [hour, minute] = legacy[2].split(":").map(Number);
      const probe = new Date(year, month - 1, day, hour, minute);
      if (
        probe.getFullYear() === year &&
        probe.getMonth() === month - 1 &&
        probe.getDate() === day &&
        probe.getHours() === hour &&
        probe.getMinutes() === minute
      ) {
        return `${legacy[1]}T${legacy[2]}`;
      }
    }
  }
  const parsed = new Date(stored);
  if (isNaN(parsed.getTime())) return "";
  return format(parsed, "yyyy-MM-dd'T'HH:mm");
}

const JobsPage = () => {
  const { jobsRef, user, region } = useContext(AuthContext);
  const location = useLocation();
  const history = useHistory();
  const shopUser = hasShopUserClaims(user?.claims);
  const sot = shopJobsSotActive(location.search, { shopUser });
  const preview = shopJobsFixtureSurface(location.search, { shopUser });
  const createSot = shopCreateSotActive(location.search, { shopUser });
  const createPreview = shopCreatePreviewSurface(location.search);
  const createGuard = createSot || createPreview;
  const trueEmptySot = shopTrueEmptySotActive(location.search, { shopUser });
  const trueEmptyPreview = shopTrueEmptyPreviewSurface(location.search);
  const trueEmptyWalk = shopTrueEmptyWalkKind(trueEmptySot, trueEmptyPreview);
  const [jobs, setJobs] = useState([]);
  const [jobsError, setJobsError] = useState("");
  const [offline, setOffline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine === false : false
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [activeJob, setActiveJob] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [createJobModalVisibility, setCreateJobModalVisibility] =
    useState(false);

  const [openTab, setOpenTab] = React.useState(() =>
    shopDemoWalkOpenTab({ sot, preview, search: location.search })
  );
  const previousSotRef = useRef(sot);
  const previousCreateSotRef = useRef(createSot);

  const [showCancelJobModal, setShowCancelJobModal] = useState(false);

  const [cancelationReason, setCancellationReason] = useState("");

  const jobSchema = Yup.object().shape({
    type: Yup.string().required("This field is required"),
    status: Yup.string().required("This field is required"),
    date: Yup.string().required("This input is required"),
  });

  const shopId = useMemo(() => (user ? user.claims.shopId : null), [user]);
  const regionId = useMemo(() => (user ? user.claims.regionId : null), [user]);
  const fixtureWalk = isShopWalkSot(sot) || isShopWalkSot(preview);
  const walkRegionId = shopDisplayRegionId(location.search, regionId, fixtureWalk);
  const walkRegionLabel = shopRegionLabel(
    walkRegionId,
    region?.id && region?.name ? [{ id: region.id, label: region.name }] : [],
  );
  const liveWriteRegionError = fixtureWalk ? null : shopLiveWriteRegionError(regionId);
  const createFixture = shopCreateFixtureKind(
    sot,
    preview,
    createSot,
    createPreview,
    trueEmptyWalk
  );
  const createFixtureSource = createSot || sot ? "sot" : createPreview || preview ? "preview" : trueEmptySot ? "true-empty" : trueEmptyPreview ? "true-empty-preview" : null;
  const skipLiveList = Boolean(preview || createGuard || trueEmptyWalk);
  const [loading, setLoading] = useState(true);
  const retry = useCallback(() => {
    if (!skipLiveList) {
      setOffline(
        typeof navigator !== "undefined" ? navigator.onLine === false : false
      );
    }
    setReloadToken((token) => token + 1);
  }, [skipLiveList]);

  useEffect(() => {
    if (
      shouldResetShopWalkResidue({
        previousSot: previousSotRef.current,
        nextSot: sot,
        previousCreateSot: previousCreateSotRef.current,
        nextCreateSot: createSot,
      })
    ) {
      previousSotRef.current = sot;
      previousCreateSotRef.current = createSot;
      setOpenTab(shopDemoWalkOpenTab({ sot, preview, search: location.search }));
    }
  }, [sot, preview, createSot, location.search]);

  useEffect(() => {
    if (!isShopWalkSot(sot) && !isShopWalkSot(preview)) return;
    const fromSot = shopWalkStatusFromSot(location.search);
    if (!fromSot || shopSearchHasStatus(location.search)) return;
    history.replace(`/app/jobs${shopSearchWithStatus(location.search, fromSot)}`);
  }, [sot, preview, location.search, history]);

  useEffect(() => {
    if (createGuard) {
      setCreateJobModalVisibility(true);
      return;
    }
    setCreateJobModalVisibility(false);
  }, [createGuard, sot, preview, trueEmptyWalk]);

  useEffect(() => {
    const completeFixture =
      sot === "complete-handoff" || preview === "complete-handoff"
        ? "complete-handoff"
        : null;
    const activeFixture =
      sot === "handoff" || preview === "handoff" ? "handoff" : null;
    if (
      shouldOpenShopCompleteTab({
        fixture: completeFixture,
        currentTab: openTab,
      })
    ) {
      setOpenTab(3);
    }
    if (
      shouldOpenShopActiveTab({
        fixture: activeFixture,
        currentTab: openTab,
      })
    ) {
      setOpenTab(2);
    }
  }, [sot, preview, openTab]);

  useEffect(() => {
    if (skipLiveList) return undefined;
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, [skipLiveList]);

  useEffect(() => {
    if (preview) {
      setLoading(false);
      setJobsError(
        preview === "error"
          ? sot
            ? SHOP_JOBS_SOT_ERROR
            : SHOP_JOBS_PREVIEW_ERROR
          : ""
      );
      setOffline(preview === "offline");
      setJobs(shopJobsPreviewJobs(preview));
      return undefined;
    }
    if (createGuard || trueEmptyWalk) {
      setLoading(false);
      setJobsError("");
      setOffline(false);
      setJobs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setJobsError("");
      try {
        const data = await fetchShopJobsWithTechs();
        if (cancelled) return;
        setJobs(data);
        setJobsError("");
      } catch (error) {
        if (cancelled) return;
        setJobsError(
          error?.message ||
            "There was an error fetching your jobs. Please reach out to an admin."
        );
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [preview, sot, createGuard, trueEmptyWalk, activeJob, reloadToken]);

  const formSubmit = useCallback(
    async (vals) => {
      setValidatingRealTime(true);
      if (!activeJob?.id) {
        setShowModal(false);
        setActiveJob({});
        return;
      }
      try {
        await jobsRef.doc(activeJob.id).update({
          type: vals.type,
          status: vals.status,
          date: vals.date,
        });
        setShowModal(false);
        setActiveJob({});
      } catch (error) {
        showError(error, "There was an error saving this job.");
      }
    },
    [activeJob, jobsRef]
  );

  const [validatingRealTime, setValidatingRealTime] = useState(false);

  const openCreateJobModal = () => {
    setCreateJobModalVisibility(true);
  };

  const closeCreateJobModal = () => {
    setCreateJobModalVisibility(false);
  };

  const handleCreatedJob = () => {
    setCreateJobModalVisibility(false);
    setReloadToken((token) => token + 1);
  };

  const handleCalendarUpdate = (formik, event) => {
    const dateTime = event.target.value;
    const timestamp = Date.parse(dateTime);
    if (isNaN(timestamp) === false) {
      const formattedTime = format(new Date(dateTime), "yyyy-MM-dd' 'HH:mm");
      formik.setFieldValue("date", formattedTime);
    }
  };

  const jobsSurface = statusSurfaceState({
    loading,
    error: jobsError,
    offline,
    count: jobs.length,
  });

  const headerDetail = jobsHeaderDetail({
    surface: jobsSurface,
    pendingCount: jobs.filter((job) => job.status === "pending").length,
    activeCount: jobs.filter((job) => job.status === "active").length,
    completeCount: jobs.filter((job) => job.status === "complete").length,
    loadedCount: jobs.length,
  });

  const emptyKind = jobsEmptyCopyKind({
    sot,
    preview,
    trueEmptyWalk,
  });
  const listCopy = createGuard ? null : jobsListCopy(jobsSurface, emptyKind);
  const createNotice =
    shopTrueEmptyFixtureNotice(location.search, { shopUser }) ||
    shopCreateFixtureNotice(location.search, { shopUser }) ||
    shopJobsFixtureNotice(location.search, { shopUser }) ||
    (preview ? (sot ? SHOP_JOBS_SOT_NOTICE : SHOP_JOBS_PREVIEW_NOTICE) : null);
  const liveEmpty = jobsSurface === "empty" && !createFixture;

  const loadedJobs = jobsSurface === "ready" ? jobs : [];

  const activeJobs = loadedJobs.filter((job) => job.status === "active");

  const pendingJobs = loadedJobs.filter((job) => job.status === "pending");

  const completeJobs = loadedJobs.filter((job) => job.status === "complete");

  const cancelledJobs = loadedJobs.filter((job) => job.status === "cancelled");

  const calendarJobs = loadedJobs.filter(
    (job) => job.status === "pending" || job.status === "active"
  );

  const pendingTabEmpty =
    jobsSurface === "ready" && !listCopy && pendingJobs.length === 0;

  const activeTabEmpty =
    jobsSurface === "ready" && !listCopy && activeJobs.length === 0;

  const completeTabEmpty =
    jobsSurface === "ready" && !listCopy && completeJobs.length === 0;

  const cancelledTabEmpty =
    jobsSurface === "ready" && !listCopy && cancelledJobs.length === 0;

  const jobTypeOptions = [
    {
      value: "Remove",
      title: "Remove",
      description: "This job is a removal.",
    },
    {
      value: "Install",
      title: "Install",
      description: "This job is an installation.",
    },
    {
      value: "Remove & Install",
      title: "Remove & Install",
      description: "This is a removal and installation.",
    },
      {
      value: "Cliff test",
      title: "Cliff test",
      description: "Test job type for Glass milestone verification.",
    }
  
  ];

  const cancelJobSubmit = (evt) => {
    evt.preventDefault();
    (async () => {
      try {
        setLoading(true);
        await jobsRef.doc(activeJob.id).update({
          status: "cancelled",
          cancellationReason: cancelationReason,
        });
        setShowCancelJobModal(false);
        setActiveJob(null);
        setCancellationReason();
        setJobs(
          [...jobs].map((job) =>
            job.id === activeJob.id ? { ...job, status: "cancelled" } : job
          )
        );
      } catch (error) {
        showError(error, "There was an error cancelling this job.");
      }
      setLoading(false);
    })();
  };

  return (
    <div
      className="container w-full mx-auto pt-20"
      data-jobs-surface={jobsSurface}
      data-testid="jobs-page"
    >
      <div className="flex flex-col">
        <button
          type="button"
          className="bg-primary-500 hover:bg-primary-900 text-white font-bold py-2 px-4 w-72 text-center mx-auto mt-10"
          onClick={() => openCreateJobModal()}
        >
          CREATE JOB
        </button>
        {liveWriteRegionError ? (
          <p className="text-center text-gray-700 mt-4 px-6" data-testid="shop-live-write-region">
            {liveWriteRegionError}
          </p>
        ) : null}
        {headerDetail ? (
          <p className="text-center text-gray-700 mt-4" data-testid="jobs-header-detail">
            {headerDetail}
          </p>
        ) : null}
        {createNotice ? (
          <p
            className="text-center text-gray-600 text-sm mt-4 px-6"
            data-testid="jobs-preview-notice"
          >
            {createNotice}
          </p>
        ) : null}
        <DemoWalk
          search={location.search}
          sot={sot}
          preview={preview}
          createSot={createSot}
          createPreview={createPreview}
          pendingCount={pendingJobs.length}
          completeCount={completeJobs.length}
          openTab={openTab}
          regionLabel={walkRegionLabel}
        />
        {isShopWalkSot(sot) || isShopWalkSot(preview) ? (
          <RegionWalkSwitcher regionId={walkRegionId} search={location.search} />
        ) : null}
        {sot ? <JobsSotSwitcher active={sot} search={location.search} /> : null}
        {trueEmptyWalk ? <TrueEmptySwitcher active="walk" /> : null}
        {createGuard ? <CreateSotSwitcher active={createGuard} /> : null}
        {listCopy ? (
          <div className="flex flex-col items-center mt-6">
            <StatusEmptyCard
              copy={listCopy}
              detail={jobsSurface === "error" ? jobsError : undefined}
              onAction={
                jobsSurface === "empty"
                  ? openCreateJobModal
                  : retry
              }
            />
          </div>
        ) : null}
        <div className="overflow-x-auto mx-8 py-8">
          {jobsSurface === "ready" ? (
            <Calendar
              onEventClick={(e) => {
                const thisActiveJob = jobs.find(({ id }) => id === e);
                if (thisActiveJob) {
                  setActiveJob(thisActiveJob);
                  setShowModal(true);
                }
              }}
              events={calendarJobs}
            />
          ) : null}
          {jobsSurface === "ready" ? (
          <div className="flex flex-wrap pl-2.5 pr-2.5 mt-6">
            <div className="w-full">
              <ul
                className=" mb-0 list-none pt-3 pb-4 grid grid-cols-4 gap-4"
                data-open-tab={openTab}
                data-testid="jobs-tabs"
                role="tablist"
              >
                <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                  <button
                    className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                      openTab === 1
                        ? "text-white bg-yellow-400"
                        : "text-blue-600 bg-white"
                    }`}
                    onClick={() => {
                      setOpenTab(1);
                      history.replace(shopWalkStatusHref(location.search, "pending"));
                    }}
                  >
                    Pending
                  </button>
                </li>
                <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                  <button
                    className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                      openTab === 2
                        ? "text-white bg-blue-500"
                        : "text-blue-600 bg-white"
                    }`}
                    onClick={() => {
                      setOpenTab(2);
                      history.replace(shopWalkStatusHref(location.search, "active"));
                    }}
                  >
                    Active
                  </button>
                </li>
                <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                  <button
                    className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                      openTab === 3
                        ? "text-white bg-green-500"
                        : "text-blue-600 bg-white"
                    }`}
                    onClick={() => {
                      setOpenTab(3);
                      history.replace(shopWalkStatusHref(location.search, "complete"));
                    }}
                  >
                    Complete
                  </button>
                </li>
                <li className="-mb-px mr-2 last:mr-0 flex-auto text-center relative">
                  <button
                    className={`text-xs font-bold uppercase px-5 py-3 shadow-lg rounded block leading-normal w-full ${
                      openTab === 4
                        ? "text-white bg-primary-500"
                        : "text-blue-600 bg-white"
                    }`}
                    onClick={() => {
                      setOpenTab(4);
                    }}
                  >
                    Cancelled
                  </button>
                </li>
              </ul>
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 rounded">
                <div className="px-1 flex-auto">
                  <div className="tab-content tab-space flex flex-col items-center">
                    <div
                      className={openTab === 1 ? "block w-full" : "hidden"}
                      id="pending"
                    >
                      {pendingTabEmpty ? (
                        <StatusEmptyCard
                          copy={JOBS_TAB_EMPTY.pending}
                          onAction={openCreateJobModal}
                        />
                      ) : null}
                      {jobsSurface === "ready" && !pendingTabEmpty ? (
                        <JobsTableComponent
                          setShowModal={setShowModal}
                          setActiveJob={setActiveJob}
                          setShowCancelJobModal={setShowCancelJobModal}
                          jobs={pendingJobs}
                        />
                      ) : null}
                    </div>
                    <div
                      className={openTab === 2 ? "block w-full" : "hidden"}
                      id="active"
                    >
                      {activeTabEmpty ? (
                        <StatusEmptyCard
                          copy={JOBS_TAB_EMPTY.active}
                          onAction={openCreateJobModal}
                        />
                      ) : null}
                      {jobsSurface === "ready" && !activeTabEmpty ? (
                        <JobsTableComponent
                          setShowModal={setShowModal}
                          setActiveJob={setActiveJob}
                          setShowCancelJobModal={setShowCancelJobModal}
                          jobs={activeJobs}
                        />
                      ) : null}
                    </div>
                    <div
                      className={openTab === 3 ? "block w-full" : "hidden"}
                      id="complete"
                    >
                      {completeTabEmpty ? (
                        <StatusEmptyCard
                          copy={JOBS_TAB_EMPTY.complete}
                          onAction={openCreateJobModal}
                        />
                      ) : null}
                      {jobsSurface === "ready" && !completeTabEmpty ? (
                        <JobsTableComponent
                          setShowModal={setShowModal}
                          setActiveJob={setActiveJob}
                          setShowCancelJobModal={setShowCancelJobModal}
                          jobs={completeJobs}
                        />
                      ) : null}
                    </div>
                    <div
                      className={openTab === 4 ? "block w-full" : "hidden"}
                      id="cancelled"
                    >
                      {cancelledTabEmpty ? (
                        <StatusEmptyCard
                          copy={JOBS_TAB_EMPTY.cancelled}
                          onAction={openCreateJobModal}
                        />
                      ) : null}
                      {jobsSurface === "ready" && !cancelledTabEmpty ? (
                        <CancelledJobsComponent jobs={cancelledJobs} />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          ) : null}
        </div>

        {jobsSurface === "loading" && (
          <div className="text-center position-absolute;">
            <Loader />
          </div>
        )}
      </div>
      {createJobModalVisibility && (
        <CreateJobModal
          key={createGuard || "live"}
          shopUser={shopUser}
          shopId={shopId}
          regionId={regionId}
          offline={offline}
          fixture={createFixture}
          fixtureSource={createFixtureSource}
          liveEmpty={liveEmpty}
          regionPhone={region?.phone}
          onClose={closeCreateJobModal}
          onCreated={handleCreatedJob}
        />
      )}
      {showModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="relative p-6 flex-auto">
                  <div className="w-full max-w-lg">
                    <div className="flex flex-wrap -mx-3 mb-6">
                      <div className="w-full md:w-1/2 px-3">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          htmlFor="grid-assignee"
                        >
                          Assignee
                        </label>
                        <p
                          className="appearance-none bg-transparent border-none w-full text-gray-700 py-3 leading-tight focus:outline-none"
                          id="grid-assignee"
                        >
                          {activeJob.tech?.name || "None Assigned"}
                        </p>
                      </div>
                      <div className="w-full md:w-1/2 px-3">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          htmlFor="grid-vehicle"
                        >
                          Vehicle Details
                        </label>
                        <p
                          className="appearance-none bg-transparent border-none w-full text-gray-700 py-3 leading-tight focus:outline-none"
                          id="grid-vehicle"
                        >
                          {shopJobVehicleLabel(activeJob.vehicle)}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap -mx-3 mb-6">
                      <div className="w-full md:w-1/3 px-3">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          htmlFor="grid-insurance"
                        >
                          Insurance
                        </label>
                        <p
                          className="appearance-none bg-transparent border-none w-full text-gray-700 py-3 leading-tight focus:outline-none"
                          id="grid-insurance"
                        >
                          {activeJob.vehicle?.insurance}
                        </p>
                      </div>
                      <div className="w-full md:w-1/3 px-3">
                        <label
                          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                          htmlFor="grid-contact"
                        >
                          Contact
                        </label>
                        <p
                          className="appearance-none bg-transparent border-none w-full text-gray-700 py-3 leading-tight focus:outline-none"
                          id="grid-contact"
                        >
                          {activeJob.contact}
                        </p>
                      </div>
                    </div>
                    <Formik
                      initialValues={{
                        type: activeJob?.type,
                        status: activeJob?.status,
                        date: activeJob?.date,
                      }}
                      onSubmit={formSubmit}
                      validationSchema={jobSchema}
                      validateOnBlur={false}
                      validateOnChange={validatingRealTime}
                    >
                      {(props) => (
                        <div className="flex flex-wrap">
                          <div className="w-full md:w-1/3 pr-1 mb-6 md:mb-0">
                            <label
                              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                              htmlFor="grid-type"
                            >
                              Type: <strong>{props.values.type}</strong>
                            </label>
                            <div className="relative">
                              <select
                                className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                id="grid-type"
                                onChange={(option) => {
                                  props.handleChange("type")(
                                    option.target.value
                                  );
                                }}
                              >
                                <option
                                  className="placeholder-gray-600"
                                  value=""
                                >
                                  Select job type
                                </option>
                                {jobTypeOptions.map((type) => (
                                  <option key={type.value} value={type.value}>
                                    {type.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="w-full md:w-1/3 pr-1 mb-6 md:mb-0">
                            <label
                              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                              htmlFor="grid-status"
                            >
                              Status: <strong>{props.values.status}</strong>
                            </label>
                            <div className="relative">
                              <select
                                className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                id="grid-status"
                                onChange={(option) => {
                                  props.handleChange("status")(
                                    option.target.value
                                  );
                                }}
                              >
                                <option value="" label="Select a status" />
                                <option
                                  value="pending"
                                  label="Pending"
                                ></option>
                                <option value="active" label="Active"></option>
                                <option
                                  value="complete"
                                  label="Complete"
                                ></option>
                              </select>
                            </div>
                          </div>
                          <div className="w-full md:w-1/3">
                            <label
                              className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                              htmlFor="grid-date"
                            >
                              Date
                            </label>
                            <input
                              className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                              id="grid-date"
                              type="datetime-local"
                              name="date"
                              value={shopEditDateInputValue(props.values.date)}
                              placeholder="Date"
                              onChange={(event) =>
                                handleCalendarUpdate(props, event)
                              }
                            />
                            {props.errors.date && (
                              <p className="text-red-500 text-xs italic mt-2">
                                {props.errors.date}
                              </p>
                            )}
                          </div>

                          <div className="w-full md:w-1/2 pr-1 mb-6 mt-4 md:mb-0">
                            <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Customer Name:
                            </p>
                            <p>
                              <strong> {activeJob.customerName}</strong>
                            </p>
                          </div>

                          <div className="w-full md:w-1/2 pr-1 mb-6 mt-4 md:mb-0">
                            <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                              Notes:
                            </p>
                            <p>
                              <strong> {activeJob.notes}</strong>
                            </p>
                          </div>

                          <div className="mt-10 mx-auto text-center">
                            <button
                              className="w-full bg-green-500 text-white active:bg-green-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                              type="button"
                              onClick={() => {
                                props.handleSubmit();
                                setValidatingRealTime(true);
                              }}
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>
                      )}
                    </Formik>
                  </div>
                </div>
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setActiveJob(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
      {showCancelJobModal && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="relative p-6 flex-auto">
                  <div className="w-full max-w-lg">
                    <h3>Are you sure you want to cancel this job?</h3>
                  </div>
                  <form onSubmit={cancelJobSubmit}>
                    <div className="w-full pt-6 max-w-lg px-3">
                      <label
                        htmlFor="cancelationNotes"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Cancelation Reason
                      </label>
                      <input
                        required
                        type="text"
                        name="cancelationNotes"
                        id="cancelationNotes"
                        onChange={(event) =>
                          setCancellationReason(event.target.value)
                        }
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                    {loading && <Loader />}
                    <div className="mt-10 mx-auto text-center">
                      <button
                        className="w-full bg-green-500 text-white active:bg-green-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                        type="submit"
                      >
                        Cancel Job
                      </button>
                    </div>
                  </form>
                </div>
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <button
                    className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={() => {
                      setShowCancelJobModal(false);
                      setActiveJob(null);
                      setCancellationReason();
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </div>
  );
};
export default JobsPage;
